import { DeepMap } from 'react-hook-form';
import {
  UserIdentityInput,
  UpdateWorkerMutationVariables,
} from 'apollo/generated/client-operations';
import {
  FormInputTypes,
  TypedFormInputs,
  GridColSpan,
  getUpdateInputValueFunction,
} from 'components/form';
import { ensureNonUndefinedFields, isNotEmpty } from 'utils';
import { SelfOnboardingWorkerNameFormData } from 'containers/selfOnboarding/steps/types';

export const getFormInputs =
  (): TypedFormInputs<SelfOnboardingWorkerNameFormData> => ({
    firstName: {
      element: FormInputTypes.Field,
      label: 'First name',
      validation: { required: true },
      layout: [GridColSpan.Span6, GridColSpan.SmSpan6],
    },
    middleInitial: {
      element: FormInputTypes.Field,
      label: 'Middle initial',
      elementProps: {
        fieldType: 'custom',
        cleaveOptions: { blocks: [1] },
      },
      layout: [GridColSpan.Span6, GridColSpan.SmSpan6],
    },
    lastName: {
      element: FormInputTypes.Field,
      label: 'Last name',
      validation: { required: true },
      layout: [GridColSpan.Span6, GridColSpan.SmSpan6],
    },
    suffix: {
      element: FormInputTypes.Field,
      label: 'Suffix',
      layout: [GridColSpan.Span6, GridColSpan.SmSpan6],
    },
  });

export const getDefaultValues = (
  basicInfo: Partial<SelfOnboardingWorkerNameFormData>,
): SelfOnboardingWorkerNameFormData => {
  const { firstName, middleInitial, lastName, suffix } = basicInfo ?? {};

  return {
    firstName: firstName ?? '',
    middleInitial: middleInitial ?? '',
    lastName: lastName ?? '',
    suffix: suffix ?? '',
  };
};

export type WorkerNameUpdateInputs = {
  userIdentityInput?: Omit<UserIdentityInput, 'userAccountId'>;
  workerInput?: Omit<UpdateWorkerMutationVariables, 'workerId'>['workerInput'];
};

/**
 * Splits the dirty name fields into the two mutations that persist them: the
 * shared identity (first/last name) and the worker record (middle initial /
 * suffix). Either side is omitted when nothing on it changed.
 */
export const getUpdateInputs = (
  data: SelfOnboardingWorkerNameFormData,
  dirtyFields: DeepMap<SelfOnboardingWorkerNameFormData, true>,
): WorkerNameUpdateInputs => {
  const getUpdateInputValue = getUpdateInputValueFunction(data, dirtyFields);

  const userIdentityInput = ensureNonUndefinedFields({
    firstName: getUpdateInputValue('firstName'),
    lastName: getUpdateInputValue('lastName'),
  });
  const workerInput = ensureNonUndefinedFields({
    middleInitial: getUpdateInputValue('middleInitial'),
    suffix: getUpdateInputValue('suffix'),
  });

  return {
    userIdentityInput: isNotEmpty(userIdentityInput)
      ? userIdentityInput
      : undefined,
    workerInput: isNotEmpty(workerInput) ? workerInput : undefined,
  };
};
